import { EventDateRange } from '@/apis/eventDateRange';
import { EventTypeRecord } from '@/apis/eventTypes';
import { AutocompleteResult, TemplatePlaceholder } from '@/types';
import { AccordionField } from '@components/DrawerAccordion';
import MissingFilterValueWarning from '@pages/AdvancedSearchListCreatorPage/customWidgets/MissingFilterValueWarning';
import { MappingError } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import { Box } from '@mui/material';
import { PropsWithChildren } from 'react';
import EventCampaigns from './EventCampaigns';
import EventDateRangePicker from './EventDateRange';
import {
  EventParticipantRecordResult,
  EventRecordResult,
} from '@/types/filterCriteriaRecords';
import EventParticipants from './EventParticipants';
import Events from './Events';
import EventTypes from './EventTypes';

const MissingValueField = ({
  children,
  isAdvancedSearch,
  isMissingValue,
  isRequired,
}: PropsWithChildren<{
  isAdvancedSearch: boolean;
  isMissingValue: boolean;
  isRequired: boolean;
}>) => (
  <Box
    sx={[
      {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 1,
      },
      isAdvancedSearch ? { width: '50ch' } : {},
    ]}
  >
    {isAdvancedSearch && (
      <MissingFilterValueWarning isMissing={isMissingValue} isRequired={isRequired} />
    )}
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

type Props = {
  eventStatuses: EventParticipantRecordResult[] | TemplatePlaceholder;
  onChangeEventStatuses: (value: EventParticipantRecordResult[] | TemplatePlaceholder) => void;
  eventTypes: EventTypeRecord[] | TemplatePlaceholder;
  onChangeEventTypes: (newEventTypes: EventTypeRecord[] | TemplatePlaceholder) => void;
  eventDateRange: EventDateRange;
  onChangeEventDateRange: (newEventDateRange: EventDateRange) => void;
  events: EventRecordResult[] | TemplatePlaceholder;
  onChangeEvents: (newEvents: EventRecordResult[] | TemplatePlaceholder) => void;
  setIsEventDateFilterInvalid?: (isInvalid: boolean) => void;
  campaigns: AutocompleteResult[] | TemplatePlaceholder;
  onChangeCampaigns: (newCampaigns: AutocompleteResult[] | TemplatePlaceholder) => void;
  isAdvancedSearch?: boolean;
  errors?: MappingError[];
};

export const EventAttendance = ({
  eventStatuses,
  onChangeEventStatuses,
  eventTypes,
  onChangeEventTypes,
  eventDateRange,
  onChangeEventDateRange,
  events,
  onChangeEvents,
  setIsEventDateFilterInvalid = () => {},
  isAdvancedSearch,
  campaigns,
  onChangeCampaigns,
  errors,
}: Props) => {
  const isMissingEventTypes = !!errors?.find(({ field }) => field === 'eventTypes');
  const isMissingCampaigns = !!errors?.find(({ field }) => field === 'eventCampaigns');
  const isMissingEvents = !!errors?.find(({ field }) => field === 'events');

  return (
    <>
      <AccordionField>
        <MissingValueField
          isMissingValue={false}
          isAdvancedSearch={!!isAdvancedSearch}
          isRequired={false}
        >
          <EventParticipants
            isAdvancedSearch={isAdvancedSearch}
            value={eventStatuses}
            onChange={onChangeEventStatuses}
          />
        </MissingValueField>
      </AccordionField>
      <EventDateRangePicker
        value={eventDateRange}
        setIsFilterInvalid={setIsEventDateFilterInvalid}
        onChange={onChangeEventDateRange}
        isAdvancedSearch={isAdvancedSearch}
      />
      <AccordionField>
        <MissingValueField
          isMissingValue={isMissingEventTypes}
          isAdvancedSearch={!!isAdvancedSearch}
          isRequired={false}
        >
          <EventTypes
            isAdvancedSearch={isAdvancedSearch}
            value={eventTypes}
            onChange={onChangeEventTypes}
            error={isMissingEventTypes}
          />
        </MissingValueField>
      </AccordionField>
      <AccordionField>
        <MissingValueField
          isMissingValue={isMissingEvents}
          isAdvancedSearch={!!isAdvancedSearch}
          isRequired={false}
        >
          <Events
            isAdvancedSearch={isAdvancedSearch}
            value={events}
            onChange={onChangeEvents}
            error={isMissingEvents}
          />
        </MissingValueField>
      </AccordionField>
      <AccordionField>
        <MissingValueField
          isMissingValue={isMissingCampaigns}
          isAdvancedSearch={!!isAdvancedSearch}
          isRequired={false}
        >
          <EventCampaigns
            isAdvancedSearch={isAdvancedSearch}
            value={campaigns}
            onChange={onChangeCampaigns}
            error={isMissingCampaigns}
          />
        </MissingValueField>
      </AccordionField>
    </>
  );
};
